import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'bootstrap-4-react/lib/components';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import InputCalculator from '../common/InputCalculator';
import { normalizeCalculatorValue, hasUnevaluatedExpression } from '../common/calculatorValue';
import { getWalmartTaxonomy, getWalmartItemSpec, submitWalmartFeed } from '../../services/walmart.service';

// --- Pure validation helpers (extracted to reduce cognitive complexity) ---

const URI_PATTERN = /^https?:\/\/.+/i;

const validateStringConstraints = (val, schema, label) => {
    const errors = [];
    if (typeof val !== 'string') return errors;
    if (schema.minLength && val.length < schema.minLength) {
        errors.push(`${label}: minimum ${schema.minLength} characters required`);
    }
    if (schema.maxLength && val.length > schema.maxLength) {
        errors.push(`${label}: maximum ${schema.maxLength} characters allowed`);
    }
    if (schema.format === 'uri' && val && !URI_PATTERN.test(val)) {
        errors.push(`${label}: must be a valid URL (starting with http:// or https://)`);
    }
    return errors;
};

const validateNumberConstraints = (val, schema, label) => {
    const errors = [];
    const num = Number(val);
    if (Number.isNaN(num)) {
        errors.push(`${label}: must be a valid number`);
        return errors;
    }
    if (schema.minimum !== undefined && num < schema.minimum) {
        errors.push(`${label}: minimum value is ${schema.minimum}`);
    }
    if (schema.maximum !== undefined && num > schema.maximum) {
        errors.push(`${label}: maximum value is ${schema.maximum}`);
    }
    if (schema.type === 'integer' && !Number.isInteger(num)) {
        errors.push(`${label}: must be a whole number`);
    }
    if (schema.multipleOf && Math.round(num / schema.multipleOf) * schema.multipleOf !== num) {
        errors.push(`${label}: must be a multiple of ${schema.multipleOf}`);
    }
    return errors;
};

const validateSubFieldEntries = (entries, parentSchema, parentLabel) => {
    const errors = [];
    for (const [subKey, subVal] of entries) {
        if (!subVal && subVal !== 0) continue;
        const subSchema = parentSchema[subKey];
        if (!subSchema) continue;
        const subLabel = `${parentLabel} → ${subSchema.title || subKey}`;
        if (typeof subVal === 'string') {
            errors.push(...validateStringConstraints(subVal, subSchema, subLabel));
        }
        if ((subSchema.type === 'number' || subSchema.type === 'integer') && subVal !== '') {
            errors.push(...validateNumberConstraints(subVal, subSchema, subLabel));
        }
    }
    return errors;
};

const validateRequiredSubFields = (subValues, requiredKeys, properties, parentLabel) => {
    const errors = [];
    for (const subKey of requiredKeys) {
        const subVal = subValues?.[subKey];
        if (!subVal && subVal !== 0) {
            errors.push(`${parentLabel} → ${properties[subKey]?.title || subKey}: required`);
        }
    }
    return errors;
};

const WalmartProductCreate = () => {
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.auth.user);
    const companyList = useSelector((state) => state.auth.companyList);

    // Seller context — same pattern as WalmartFeeds
    const companySellerId = useMemo(() => {
        const currentCompany = JSON.parse(localStorage.getItem('currentCompany') || '{}');
        if (currentCompany?.companySellerId) return currentCompany.companySellerId;
        if (userDetails?.Value?.companySellerId && userDetails.Value.companySellerId !== 0) {
            return userDetails.Value.companySellerId;
        }
        if (companyList?.length > 0) return companyList[0].companySellerId;
        return null;
    }, [userDetails, companyList]);

    // Step tracking: 'category' → 'fields' → 'review'
    const [step, setStep] = useState('category');

    // Taxonomy state
    const [taxonomy, setTaxonomy] = useState(null);
    const [loadingTaxonomy, setLoadingTaxonomy] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});

    // Spec / fields state
    const [loadingSpec, setLoadingSpec] = useState(false);
    const [fields, setFields] = useState({});
    const [requiredFields, setRequiredFields] = useState([]);
    const [allOfRules, setAllOfRules] = useState([]);

    // Base item fields (always required)
    const [baseFields, setBaseFields] = useState({
        sku: '',
        productName: '',
        shortDescription: '',
        brand: '',
        mainImageUrl: '',
        price: '',
        productIdType: 'UPC',
        productId: '',
        shippingWeight: '1',
    });

    // Submission
    const [submitting, setSubmitting] = useState(false);

    let idSeq = 0;
    const uid = () => `k${++idSeq}-${Date.now()}`;

    // Load taxonomy on mount
    useEffect(() => {
        if (companySellerId) {
            loadTaxonomy();
        }
    }, [companySellerId]);

    const loadTaxonomy = async () => {
        setLoadingTaxonomy(true);
        const result = await getWalmartTaxonomy(companySellerId, 'MP_ITEM');
        // Response shape: { payload: [{ categoryName, categoryId }], ... }
        // OR: { itemTaxonomy: [...] } (older format)
        if (result?.payload) {
            setTaxonomy(result.payload);
        } else if (result?.itemTaxonomy) {
            setTaxonomy(result.itemTaxonomy);
        } else {
            toast.error(result?.message || 'Failed to load Walmart product categories');
        }
        setLoadingTaxonomy(false);
    };

    // Extract product types from a single group
    const extractGroupTypes = (group, category) =>
        (group.productType || []).map(pt => ({
            name: pt.productTypeName,
            category,
            group: group.productTypeGroupName || '',
        }));

    // Build flat list of product types for search
    const getAllProductTypes = useCallback(() => {
        if (!taxonomy) return [];
        return taxonomy.flatMap(cat =>
            (cat.productTypeGroup || []).flatMap(group => extractGroupTypes(group, cat.category))
        );
    }, [taxonomy]);

    // Filter product types by search
    const filteredProductTypes = useCallback(() => {
        const all = getAllProductTypes();
        if (!searchTerm.trim()) return all;
        const term = searchTerm.toLowerCase();
        return all.filter(pt =>
            pt.name.toLowerCase().includes(term) ||
            pt.category.toLowerCase().includes(term)
        );
    }, [getAllProductTypes, searchTerm]);

    // Toggle category expand/collapse
    const toggleCategory = (catName) => {
        setExpandedCategories(prev => ({ ...prev, [catName]: !prev[catName] }));
    };

    // Select product type and move to fields step
    const handleSelectProductType = async (productTypeName) => {
        setSelectedProductType(productTypeName);
        setStep('fields');
        setLoadingSpec(true);

        // Fetch full spec for this product type
        const specData = await getWalmartItemSpec(companySellerId, [productTypeName], null, 'MP_ITEM');

        if (specData?.schema) {
            parseSpecFields(specData.schema, productTypeName);
        } else if (specData?.fieldSchemas) {
            // Fallback if fieldSchemas returned (shouldn't happen without fieldNames)
            buildFieldsFromSchemas(specData.fieldSchemas);
        } else {
            toast.error(specData?.message || 'Failed to load product spec');
            setStep('category');
        }
        setLoadingSpec(false);
    };

    // Parse the full spec response to extract visible fields for the product type
    const parseSpecFields = (schema, productTypeName) => {
        const itemProps = schema?.properties?.MPItem?.items?.properties || {};
        const visibleProps = itemProps.Visible?.properties || {};
        const ptSchema = visibleProps[productTypeName];

        if (!ptSchema?.properties) {
            toast.error('No fields found for this product type');
            setStep('category');
            return;
        }

        const props = ptSchema.properties;

        // Find required fields from schema
        const reqFields = ptSchema.required || [];

        // Also check allOf conditions for additional required fields
        const allOfRequired = new Set();
        const conditionalRules = [];
        (ptSchema.allOf || []).forEach(rule => {
            if (rule.required && !rule.if) {
                // Unconditional required (no "if")
                rule.required.forEach(f => allOfRequired.add(f));
            } else if (rule.if && rule.then) {
                // Conditional required — store for runtime evaluation
                conditionalRules.push(rule);
            }
        });
        setAllOfRules(conditionalRules);

        // Base visible fields that are already in baseFields — skip them in the spec form
        const BASE_FIELD_KEYS = new Set(['productName', 'shortDescription', 'brand', 'mainImageUrl']);

        const initialFields = {};
        const reqList = [];

        Object.entries(props).forEach(([fieldName, fieldSchema]) => {
            if (BASE_FIELD_KEYS.has(fieldName)) return; // handled in base fields section

            const isRequired = reqFields.includes(fieldName) || allOfRequired.has(fieldName);
            const fieldData = buildFieldFromSchema(fieldName, fieldSchema);
            initialFields[fieldName] = fieldData;

            if (isRequired) {
                reqList.push(fieldName);
            }
        });

        setFields(initialFields);
        setRequiredFields(reqList);
    };

    // Build fields from flat schemas (fallback)
    const buildFieldsFromSchemas = (schemas) => {
        const initialFields = {};
        Object.entries(schemas).forEach(([fieldName, schema]) => {
            initialFields[fieldName] = buildFieldFromSchema(fieldName, schema);
        });
        setFields(initialFields);
    };

    // ---- Field builders (same pattern as WalmartFeedDetail) ----

    const buildFieldFromSchema = (_fieldName, schema) => {
        if (!schema) return { type: 'string', value: '', schema: null };

        if (schema.type === 'array' && schema.items?.type === 'object' && schema.items?.properties) {
            const subProps = schema.items.properties;
            const emptyRow = { _key: uid() };
            Object.entries(subProps).forEach(([key]) => { emptyRow[key] = ''; });
            return { type: 'objectArray', rows: [emptyRow], schema };
        }

        if (schema.type === 'array') {
            return { type: 'array', values: [{ _key: uid(), value: '' }], schema };
        }

        if (schema.type === 'object' && schema.properties) {
            const subValues = {};
            Object.entries(schema.properties).forEach(([key]) => { subValues[key] = ''; });
            return { type: 'object', subValues, schema };
        }

        if (schema.enum) {
            return { type: 'enum', value: '', schema };
        }

        if (schema.type === 'number' || schema.type === 'integer') {
            return { type: 'number', value: '', schema };
        }

        if (schema.type === 'boolean') {
            return { type: 'boolean', value: 'true', schema };
        }

        return { type: 'string', value: '', schema };
    };

    // ---- Field change handlers ----

    const handleFieldChange = (field, value) => {
        setFields(prev => ({ ...prev, [field]: { ...prev[field], value } }));
    };

    const handleObjectFieldChange = (field, subKey, value) => {
        setFields(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                subValues: { ...prev[field]?.subValues, [subKey]: value },
            },
        }));
    };

    const handleObjectArrayChange = (field, rowIndex, subKey, value) => {
        setFields(prev => {
            const rows = [...(prev[field]?.rows || [])];
            rows[rowIndex] = { ...rows[rowIndex], [subKey]: value };
            return { ...prev, [field]: { ...prev[field], rows } };
        });
    };

    const handleAddObjectArrayRow = (field) => {
        setFields(prev => {
            const schema = prev[field]?.schema;
            const subProps = schema?.items?.properties || {};
            const emptyRow = { _key: uid() };
            Object.keys(subProps).forEach(key => { emptyRow[key] = ''; });
            const rows = [...(prev[field]?.rows || []), emptyRow];
            return { ...prev, [field]: { ...prev[field], rows } };
        });
    };

    const handleRemoveObjectArrayRow = (field, rowIndex) => {
        setFields(prev => {
            const rows = (prev[field]?.rows || []).filter((_, i) => i !== rowIndex);
            if (rows.length === 0) {
                const schema = prev[field]?.schema;
                const subProps = schema?.items?.properties || {};
                const emptyRow = { _key: uid() };
                Object.keys(subProps).forEach(key => { emptyRow[key] = ''; });
                rows.push(emptyRow);
            }
            return { ...prev, [field]: { ...prev[field], rows } };
        });
    };

    const handleArrayValueChange = (field, index, value) => {
        setFields(prev => {
            const arr = [...(prev[field]?.values || [])];
            arr[index] = { ...arr[index], value };
            return { ...prev, [field]: { ...prev[field], values: arr } };
        });
    };

    const handleAddArrayValue = (field) => {
        setFields(prev => {
            const arr = [...(prev[field]?.values || []), { _key: uid(), value: '' }];
            return { ...prev, [field]: { ...prev[field], values: arr } };
        });
    };

    const handleRemoveArrayValue = (field, index) => {
        setFields(prev => {
            let arr = (prev[field]?.values || []).filter((_, i) => i !== index);
            if (arr.length === 0) arr = [{ _key: uid(), value: '' }];
            return { ...prev, [field]: { ...prev[field], values: arr } };
        });
    };

    // ---- Build final value from field data ----

    const buildFieldValue = (fieldData) => {
        if (!fieldData) return undefined;
        switch (fieldData.type) {
            case 'enum':
            case 'string':
                return fieldData.value || undefined;
            case 'number':
                return fieldData.value !== '' ? Number(fieldData.value) : undefined;
            case 'boolean':
                return fieldData.value === 'true';
            case 'array': {
                const vals = (fieldData.values || []).map(v => v.value).filter(v => v !== '');
                return vals.length > 0 ? vals : undefined;
            }
            case 'object': {
                const obj = {};
                let hasValue = false;
                Object.entries(fieldData.subValues || {}).forEach(([k, v]) => {
                    const propSchema = fieldData.schema?.properties?.[k];
                    if (v !== '' && v !== undefined) {
                        obj[k] = (propSchema?.type === 'number' || propSchema?.type === 'integer') ? Number(v) : v;
                        hasValue = true;
                    }
                });
                return hasValue ? obj : undefined;
            }
            case 'objectArray': {
                const rows = (fieldData.rows || []).filter(row =>
                    Object.entries(row).some(([k, v]) => k !== '_key' && v !== '' && v !== undefined)
                );
                if (rows.length === 0) return undefined;
                // Convert numeric sub-fields
                const itemProps = fieldData.schema?.items?.properties || {};
                return rows.map(row => {
                    const cleaned = {};
                    Object.entries(row).forEach(([k, v]) => {
                        if (k !== '_key' && v !== '' && v !== undefined) {
                            cleaned[k] = (itemProps[k]?.type === 'number' || itemProps[k]?.type === 'integer') ? Number(v) : v;
                        }
                    });
                    return cleaned;
                });
            }
            default:
                return fieldData.value || undefined;
        }
    };

    // ---- Submit ----

    // Validate a single field value against its schema constraints
    const validateFieldValue = (fieldName, fieldData) => {
        const schema = fieldData?.schema;
        if (!schema) return [];
        const title = schema.title || friendlyFieldName(fieldName);
        const val = buildFieldValue(fieldData);

        // Required check is handled separately — skip if empty here
        if (val === undefined || val === '' || val === null) return [];

        if (fieldData.type === 'string' || fieldData.type === 'enum') {
            return validateStringConstraints(val, schema, title);
        }

        if (fieldData.type === 'number') {
            return validateNumberConstraints(val, schema, title);
        }

        if (fieldData.type === 'array' && Array.isArray(val)) {
            return validateArrayField(val, schema, title);
        }

        if (fieldData.type === 'object' && schema.properties) {
            return validateObjectField(fieldData, schema, title);
        }

        if (fieldData.type === 'objectArray' && Array.isArray(val)) {
            return validateObjectArrayField(val, schema, title);
        }

        return [];
    };

    const validateArrayField = (val, schema, title) => {
        const errors = [];
        if (schema.minItems && val.length < schema.minItems) {
            errors.push(`${title}: at least ${schema.minItems} item(s) required`);
        }
        const itemSchema = schema.items;
        if (!itemSchema) return errors;
        val.forEach((item, i) => {
            if (typeof item === 'string') {
                errors.push(...validateStringConstraints(item, itemSchema, `${title} item ${i + 1}`));
            }
        });
        return errors;
    };

    const validateObjectField = (fieldData, schema, title) => {
        const errors = [];
        errors.push(...validateRequiredSubFields(fieldData.subValues, schema.required || [], schema.properties, title));
        errors.push(...validateSubFieldEntries(Object.entries(fieldData.subValues || {}), schema.properties, title));
        return errors;
    };

    const validateObjectArrayField = (val, schema, title) => {
        const errors = [];
        if (schema.minItems && val.length < schema.minItems) {
            errors.push(`${title}: at least ${schema.minItems} entry/entries required`);
        }
        const itemProps = schema.items?.properties || {};
        const itemReq = schema.items?.required || [];
        val.forEach((row, ri) => {
            const entryLabel = `${title} entry ${ri + 1}`;
            errors.push(...validateRequiredSubFields(row, itemReq, itemProps, entryLabel));
            errors.push(...validateSubFieldEntries(Object.entries(row), itemProps, entryLabel));
        });
        return errors;
    };

    const getBaseFieldErrors = () => {
        const errors = [];
        const requiredBase = { SKU: 'sku', 'Product Name': 'productName', Price: 'price', 'Product ID': 'productId', Brand: 'brand', 'Main Image URL': 'mainImageUrl' };
        for (const [label, key] of Object.entries(requiredBase)) {
            const val = typeof baseFields[key] === 'string' ? baseFields[key].trim() : baseFields[key];
            if (!val) errors.push(label);
        }
        if (baseFields.productName.trim() && baseFields.productName.trim().length < 10) {
            errors.push('Product Name (minimum 10 characters)');
        }
        if (hasUnevaluatedExpression(baseFields.price)) {
            errors.push('Price (must be a number)');
        }
        if (baseFields.mainImageUrl.trim() && !URI_PATTERN.test(baseFields.mainImageUrl.trim())) {
            errors.push('Main Image URL (must be a valid URL)');
        }
        return errors;
    };

    const getSpecFieldErrors = () => {
        return requiredFields
            .filter(fieldName => {
                const val = buildFieldValue(fields[fieldName]);
                return val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
            })
            .map(fieldName => fields[fieldName]?.schema?.title || fieldName);
    };

    const getConstraintErrors = () => {
        return Object.entries(fields).flatMap(([fieldName, fieldData]) => validateFieldValue(fieldName, fieldData));
    };

    const getConditionalFieldErrors = () => {
        const errors = [];
        for (const rule of allOfRules) {
            const ifProps = rule.if?.properties || {};
            // Check if ALL if-conditions are met
            const conditionMet = Object.entries(ifProps).every(([fieldName, fieldSchema]) => {
                const val = buildFieldValue(fields[fieldName]) ?? baseFields[fieldName];
                if (!val) return false;
                if (fieldSchema.enum) return fieldSchema.enum.includes(val);
                return true;
            });
            if (!conditionMet) continue;
            // Condition met — check then.required fields
            for (const requiredField of (rule.then?.required || [])) {
                const fieldData = fields[requiredField];
                const val = fieldData ? buildFieldValue(fieldData) : undefined;
                if (val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
                    const title = fieldData?.schema?.title || requiredField.replaceAll('_', ' ').replaceAll(/\b\w/g, c => c.toUpperCase());
                    errors.push(title);
                }
            }
        }
        return errors;
    };

    const handleSubmit = async () => {
        const missingBase = getBaseFieldErrors();
        if (missingBase.length > 0) {
            toast.error(`Please fix base fields: ${missingBase.join(', ')}`);
            return;
        }

        const missingSpec = getSpecFieldErrors();
        if (missingSpec.length > 0) {
            toast.error(`Please fill required fields: ${missingSpec.join(', ')}`);
            return;
        }

        const missingConditional = getConditionalFieldErrors();
        if (missingConditional.length > 0) {
            toast.error(`Please fill required fields: ${missingConditional.join(', ')}`);
            return;
        }

        const validationErrors = getConstraintErrors();
        if (validationErrors.length > 0) {
            toast.error(validationErrors[0]);
            if (validationErrors.length > 1) {
                toast.warn(`${validationErrors.length - 1} more validation error(s)`);
            }
            return;
        }

        // Build Visible section
        const visible = {};
        // Add base visible fields
        visible.productName = baseFields.productName;
        visible.shortDescription = baseFields.shortDescription || baseFields.productName;
        visible.brand = baseFields.brand;
        visible.mainImageUrl = baseFields.mainImageUrl;

        // Add spec fields
        Object.entries(fields).forEach(([fieldName, fieldData]) => {
            const val = buildFieldValue(fieldData);
            if (val !== undefined) {
                visible[fieldName] = val;
            }
        });

        const feedData = {
            MPItemFeedHeader: {
                feedType: 'MP_ITEM',
                processMode: 'REPLACE',
                locale: 'en',
                subset: 'EXTERNAL',
                subCategory: 'product_content_and_site_exp',
                version: '5.0.20260114-19_40_57',
            },
            MPItem: [{
                Orderable: {
                    sku: baseFields.sku,
                    productIdentifiers: {
                        productIdType: baseFields.productIdType,
                        productId: baseFields.productId,
                    },
                    specProductType: selectedProductType,
                    price: Number(baseFields.price),
                    ShippingWeight: Number(baseFields.shippingWeight) || 1,
                    country_of_origin_substantial_transformation: 'United States',
                },
                Visible: {
                    [selectedProductType]: visible,
                },
            }],
        };

        setSubmitting(true);
        const result = await submitWalmartFeed(companySellerId, feedData, 'MP_ITEM');
        setSubmitting(false);

        if (result?.feedId) {
            toast.success(`Feed submitted! Feed ID: ${result.feedId}`);
            navigate(`/walmart-feeds/detail/${result.feedId}`);
        } else {
            toast.error(result?.message || 'Failed to submit feed');
        }
    };

    // ---- Render helpers ----

    const friendlyFieldName = (field) =>
        field.replaceAll(/([A-Z])/g, ' $1').replaceAll('_', ' ').replace(/^\w/, c => c.toUpperCase()).trim();

    const renderSubFieldInput = (value, onChange, propSchema) => {
        if (propSchema?.enum) {
            return (
                <select className="form-control form-control-sm" value={value} onChange={onChange}>
                    <option value="">-- Select --</option>
                    {propSchema.enum.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        }
        if (propSchema?.type === 'number' || propSchema?.type === 'integer') {
            return (
                <input type="number" className="form-control form-control-sm" value={value}
                    onChange={onChange} placeholder={propSchema?.title || 'Number'}
                    min={propSchema?.minimum} max={propSchema?.maximum}
                    step={propSchema?.multipleOf || 'any'} />
            );
        }
        return (
            <input type="text" className="form-control form-control-sm" value={value}
                onChange={onChange}
                placeholder={propSchema?.title || 'Enter value'}
                maxLength={propSchema?.maxLength} />
        );
    };

    const renderObjectArrayRow = (field, schema, row, ri, totalRows) => (
        <div key={row._key} className="border rounded p-2 mb-2" style={{ backgroundColor: '#fff' }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: '12px', fontWeight: 500 }}>Entry {ri + 1}</span>
                {totalRows > 1 && (
                    <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1"
                        onClick={() => handleRemoveObjectArrayRow(field, ri)} title="Remove">
                        <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                )}
            </div>
            {Object.entries(schema.items.properties).map(([subKey, subSchema]) => (
                <div key={subKey} className="row mb-1 align-items-center">
                    <div className="col-4">
                        <label className="mb-0" style={{ fontSize: '12px', fontWeight: 500 }}>
                            {subSchema.title || subKey}
                            {schema.items.required?.includes(subKey) && <span className="text-danger ml-1">*</span>}
                        </label>
                    </div>
                    <div className="col-8">
                        {renderSubFieldInput(
                            row[subKey] || '',
                            (e) => handleObjectArrayChange(field, ri, subKey, e.target.value),
                            subSchema
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderFieldInput = (field, fieldData) => {
        const schema = fieldData.schema;
        const title = schema?.title || friendlyFieldName(field);
        const isRequired = requiredFields.includes(field);

        return (
            <div key={field} className="mb-3 p-2 border rounded" style={{ backgroundColor: '#fafbfc' }}>
                <div className="mb-1">
                    <span className="input-label fnt-wgt-editmgmt">{title}</span>
                    {isRequired && <span className="text-danger ml-1">*</span>}
                </div>
                {schema?.description && (
                    <p className="mb-2" style={{ fontSize: '11px', color: '#888' }}>
                        {schema.description.substring(0, 150)}{schema.description.length > 150 ? '...' : ''}
                    </p>
                )}

                {/* Object with sub-fields */}
                {fieldData.type === 'object' && schema?.properties && (
                    <div className="ml-2">
                        {Object.entries(schema.properties).map(([subKey, subSchema]) => (
                            <div key={subKey} className="row mb-1 align-items-center">
                                <div className="col-4">
                                    <label className="mb-0" style={{ fontSize: '12px', fontWeight: 500 }}>
                                        {subSchema.title || subKey}
                                        {schema.required?.includes(subKey) && <span className="text-danger ml-1">*</span>}
                                    </label>
                                </div>
                                <div className="col-8">
                                    {renderSubFieldInput(
                                        fieldData.subValues?.[subKey] || '',
                                        (e) => handleObjectFieldChange(field, subKey, e.target.value),
                                        subSchema
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Array of objects */}
                {fieldData.type === 'objectArray' && schema?.items?.properties && (
                    <div className="ml-2">
                        {(fieldData.rows || []).map((row, ri) =>
                            renderObjectArrayRow(field, schema, row, ri, fieldData.rows.length)
                        )}
                        <button type="button" className="btn cus-seconday-bg-btn round btn-sm"
                            onClick={() => handleAddObjectArrayRow(field)} title="Add entry">
                            <i className="fa fa-plus" aria-hidden="true"></i>
                        </button>
                    </div>
                )}

                {/* Enum dropdown */}
                {fieldData.type === 'enum' && (
                    <select className="form-control" value={fieldData.value}
                        onChange={(e) => handleFieldChange(field, e.target.value)}>
                        <option value="">-- Select --</option>
                        {schema?.enum?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                )}

                {/* Simple string */}
                {fieldData.type === 'string' && (
                    <>
                        <input type="text" className="form-control" value={fieldData.value}
                            onChange={(e) => handleFieldChange(field, e.target.value)}
                            placeholder={schema?.description ? schema.description.substring(0, 80) : 'Enter text value'}
                            maxLength={schema?.maxLength} />
                        {(schema?.minLength || schema?.maxLength) && (
                            <small className="text-muted">
                                {schema.minLength ? `Min: ${schema.minLength}` : ''}{schema.minLength && schema.maxLength ? ' / ' : ''}{schema.maxLength ? `Max: ${schema.maxLength}` : ''} chars
                                {fieldData.value ? ` (${fieldData.value.length})` : ''}
                            </small>
                        )}
                    </>
                )}

                {/* Number */}
                {fieldData.type === 'number' && (
                    <input type="number" className="form-control" value={fieldData.value}
                        onChange={(e) => handleFieldChange(field, e.target.value)}
                        placeholder="Enter numeric value"
                        min={schema?.minimum} max={schema?.maximum}
                        step={schema?.type === 'integer' ? 1 : (schema?.multipleOf || 'any')} />
                )}

                {/* Boolean */}
                {fieldData.type === 'boolean' && (
                    <select className="form-control" value={fieldData.value}
                        onChange={(e) => handleFieldChange(field, e.target.value)}>
                        <option value="true">True</option>
                        <option value="false">False</option>
                    </select>
                )}

                {/* Simple array of strings/enums */}
                {fieldData.type === 'array' && (
                    <div>
                        {(fieldData.values || []).map((entry, vi) => (
                            <div key={entry._key} className="d-flex align-items-center mb-1">
                                {schema?.items?.enum ? (
                                    <select className="form-control" value={entry.value}
                                        onChange={(e) => handleArrayValueChange(field, vi, e.target.value)}>
                                        <option value="">-- Select --</option>
                                        {schema.items.enum.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input type="text" className="form-control" value={entry.value}
                                        onChange={(e) => handleArrayValueChange(field, vi, e.target.value)}
                                        placeholder={schema?.items?.title || `Value ${vi + 1}`}
                                        maxLength={schema?.items?.maxLength} />
                                )}
                                {fieldData.values.length > 1 && (
                                    <button type="button" className="btn btn-sm btn-outline-danger ml-1 py-0 px-1"
                                        onClick={() => handleRemoveArrayValue(field, vi)} title="Remove">
                                        <i className="fa fa-times" aria-hidden="true"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button type="button" className="btn cus-seconday-bg-btn round btn-sm mt-1"
                            onClick={() => handleAddArrayValue(field)} title="Add value">
                            <i className="fa fa-plus" aria-hidden="true"></i>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderCategoryItem = (cat) => (
        <div key={cat.category} className="mb-1">
            <button
                className="btn btn-link text-left d-block w-100 p-2"
                style={{ textDecoration: 'none', color: '#333', fontWeight: 500 }}
                onClick={() => toggleCategory(cat.category)}
            >
                <i className={`fa fa-chevron-${expandedCategories[cat.category] ? 'down' : 'right'} mr-2`}
                    aria-hidden="true" style={{ fontSize: '10px', width: '12px' }}></i>
                {cat.category}
            </button>
            {expandedCategories[cat.category] && (
                <div className="ml-4">
                    {extractGroupTypes({ productType: (cat.productTypeGroup || []).flatMap(g => g.productType || []) }, cat.category).map(pt => (
                        <button
                            key={pt.name}
                            className="btn btn-sm btn-outline-secondary mr-2 mb-2"
                            onClick={() => handleSelectProductType(pt.name)}
                        >
                            {pt.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    // ---- Render ----

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec mb-1">
                    <div>
                        Create Walmart Product
                        {selectedProductType && (
                            <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                                — {selectedProductType}
                            </span>
                        )}
                    </div>
                    <div className="top_right_button_container">
                        {step === 'fields' && (
                            <Button
                                className="cus-seconday-bg-btn top-btn"
                                onClick={() => { setStep('category'); setSelectedProductType(null); setFields({}); }}
                            >
                                <i className="fa fa-arrow-left mr-1" aria-hidden="true"></i> Change Category
                            </Button>
                        )}
                        <button
                            className="btn cus-primary-transparent-btn top-btn"
                            onClick={() => navigate('/inventory')}
                        >
                            Back
                        </button>
                    </div>
                </div>

                {/* Step 1: Category / Product Type Selection */}
                {step === 'category' && (
                    <div className="main_content cus-page-content-sec">
                        <h6 className="mb-3">Select a Product Type</h6>
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search product types (e.g. Toothpastes, T-Shirts, Batteries...)"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ maxWidth: '500px' }}
                            />
                        </div>

                        {loadingTaxonomy && (
                            <div className="text-center py-4">
                                <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
                                <p className="mt-2">Loading categories from Walmart...</p>
                            </div>
                        )}

                        {!loadingTaxonomy && taxonomy && searchTerm.trim() && (
                            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                {filteredProductTypes().length === 0 ? (
                                    <p className="text-muted">No product types match "{searchTerm}"</p>
                                ) : (
                                    <div className="list-group">
                                        {filteredProductTypes().map(pt => (
                                            <button
                                                key={pt.name}
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                onClick={() => handleSelectProductType(pt.name)}
                                            >
                                                <span><strong>{pt.name}</strong></span>
                                                <span className="badge badge-light" style={{ fontSize: '11px' }}>{pt.category}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {!loadingTaxonomy && taxonomy && !searchTerm.trim() && (
                            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                {taxonomy.map(cat => renderCategoryItem(cat))}
                            </div>
                        )}

                        {!loadingTaxonomy && !taxonomy && !companySellerId && (
                            <div className="alert alert-warning">
                                No seller context found. Please select a Company/Seller first.
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Fill Fields */}
                {step === 'fields' && (
                    <div className="main_content cus-page-content-sec">
                        {loadingSpec && (
                            <div className="text-center py-4">
                                <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
                                <p className="mt-2">Loading spec for {selectedProductType}...</p>
                            </div>
                        )}

                        {!loadingSpec && (
                            <>
                                {/* Base fields section */}
                                <div className="mb-4">
                                    <h6 className="mb-3" style={{ borderBottom: '2px solid #0071dc', paddingBottom: '6px' }}>
                                        Basic Information
                                    </h6>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="wpc-sku" className="input-label fnt-wgt-editmgmt">SKU <span className="text-danger">*</span></label>
                                                <input id="wpc-sku" type="text" className="form-control" value={baseFields.sku}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, sku: e.target.value }))}
                                                    placeholder="e.g. SKU-12345" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="wpc-productName" className="input-label fnt-wgt-editmgmt">Product Name <span className="text-danger">*</span></label>
                                                <input id="wpc-productName" type="text" className="form-control" value={baseFields.productName}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, productName: e.target.value }))}
                                                    placeholder="Product name (max 199 characters)" maxLength={199} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="form-group">
                                                <label htmlFor="wpc-shortDescription" className="input-label fnt-wgt-editmgmt">Short Description</label>
                                                <textarea id="wpc-shortDescription" className="form-control" rows={2} value={baseFields.shortDescription}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, shortDescription: e.target.value }))}
                                                    placeholder="Brief product description" maxLength={4000} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="wpc-brand" className="input-label fnt-wgt-editmgmt">Brand <span className="text-danger">*</span></label>
                                                <input id="wpc-brand" type="text" className="form-control" value={baseFields.brand}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, brand: e.target.value }))}
                                                    placeholder="Brand name" maxLength={60} />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="wpc-price" className="input-label fnt-wgt-editmgmt">Price (USD) <span className="text-danger">*</span></label>
                                                <InputCalculator id="wpc-price" inputMode="decimal" maxLength={50} value={String(baseFields.price ?? '')}
                                                    onChange={(e) => {
                                                        const value = normalizeCalculatorValue(e);
                                                        if (value !== null) setBaseFields(p => ({ ...p, price: value }));
                                                    }}
                                                    placeholder="19.99" />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="wpc-shippingWeight" className="input-label fnt-wgt-editmgmt">Shipping Weight (lbs)</label>
                                                <input id="wpc-shippingWeight" type="number" className="form-control" value={baseFields.shippingWeight}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, shippingWeight: e.target.value }))}
                                                    placeholder="1" min={0} step="0.1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="form-group">
                                                <label htmlFor="wpc-mainImageUrl" className="input-label fnt-wgt-editmgmt">Main Image URL <span className="text-danger">*</span></label>
                                                <input id="wpc-mainImageUrl" type="text" className="form-control" value={baseFields.mainImageUrl}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, mainImageUrl: e.target.value }))}
                                                    placeholder="https://example.com/image.jpg" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label htmlFor="wpc-productIdType" className="input-label fnt-wgt-editmgmt">Product ID Type <span className="text-danger">*</span></label>
                                                <select id="wpc-productIdType" className="form-control" value={baseFields.productIdType}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, productIdType: e.target.value }))}>
                                                    <option value="UPC">UPC</option>
                                                    <option value="GTIN">GTIN</option>
                                                    <option value="EAN">EAN</option>
                                                    <option value="ISBN">ISBN</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <div className="form-group">
                                                <label htmlFor="wpc-productId" className="input-label fnt-wgt-editmgmt">Product ID <span className="text-danger">*</span></label>
                                                <input id="wpc-productId" type="text" className="form-control" value={baseFields.productId}
                                                    onChange={(e) => setBaseFields(p => ({ ...p, productId: e.target.value }))}
                                                    placeholder="UPC / GTIN / EAN / ISBN" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Spec fields section */}
                                <div className="mb-4">
                                    <h6 className="mb-3" style={{ borderBottom: '2px solid #0071dc', paddingBottom: '6px' }}>
                                        {selectedProductType} Attributes
                                        <span style={{ fontSize: '12px', color: '#888', fontWeight: 400, marginLeft: '10px' }}>
                                            ({requiredFields.length} required)
                                        </span>
                                    </h6>

                                    {/* Required fields first */}
                                    {requiredFields.length > 0 && (
                                        <>
                                            <p className="mb-2" style={{ fontSize: '12px', color: '#666' }}>Required fields:</p>
                                            {requiredFields.map(fieldName => {
                                                const fieldData = fields[fieldName];
                                                if (!fieldData) return null;
                                                return renderFieldInput(fieldName, fieldData);
                                            })}
                                        </>
                                    )}

                                    {/* Optional fields */}
                                    {Object.keys(fields).filter(f => !requiredFields.includes(f)).length > 0 && (
                                        <>
                                            <p className="mb-2 mt-3" style={{ fontSize: '12px', color: '#666' }}>
                                                Optional fields ({Object.keys(fields).filter(f => !requiredFields.includes(f)).length}):
                                            </p>
                                            {Object.entries(fields)
                                                .filter(([fieldName]) => !requiredFields.includes(fieldName))
                                                .map(([fieldName, fieldData]) => renderFieldInput(fieldName, fieldData))
                                            }
                                        </>
                                    )}
                                </div>

                                {/* Submit button */}
                                <div className="d-flex justify-content-end mb-4" style={{ gap: '10px' }}>
                                    <button
                                        className="btn cus-primary-transparent-btn"
                                        onClick={() => navigate('/inventory')}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn cus-seconday-bg-btn"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        style={{ backgroundColor: '#0071dc', borderColor: '#0071dc', color: '#fff', minWidth: '200px' }}
                                    >
                                        {submitting ? (
                                            <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i> Submitting...</>
                                        ) : (
                                            <><i className="fa fa-paper-plane mr-1" aria-hidden="true"></i> Submit to Walmart</>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

export default WalmartProductCreate;
